import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Action } from '../../types/toolbar';
import { Toolbar } from '../../components/ui/toolbar/toolbar.component';
import CustomModal from '../../components/ui/modal/modal.component';
import { EditTenant } from '../../workspaces/tenantConfig/editTenant/editTenant.component';
import { ChangeSubscription } from '../../workspaces/tenantConfig/changeSubscription/changeSubscription.component';
import { useAuthContext } from '../../contexts/auth';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RestoreIcon from '@mui/icons-material/Restore';
import { TenantState } from '../../workspaces/tenantConfig/tenantState/tenantState.component';
import { AddUser } from '../../workspaces/tenantConfig/addUser/addUser';
import { useNavigate } from 'react-router-dom';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import NumbersIcon from '@mui/icons-material/Numbers';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { AddPermissionGroup } from '../../workspaces/tenantConfig/addPermissionGroup/addPermissionGroup.component';
import DynamicForm from '../../components/ui/dynamicForm/dynamicForm.component';
import StatusTransitionActionForm from '../../components/ui/statusTransitionActionForm/statusTransitionActionForm.component';
import { NUMERATION_SCHEMA_ENDPOINT, TENANT_SETTINGS_ENDPOINT } from '../../consts';
import { useHasPermission } from '../../contexts/auth';
import { GroupFields } from '../../types/dynamicForm';

export const TenantActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuthContext();
  const tenant = currentUser?.tenant;
  const [editTenantOpened, setEditTenantOpened] = useState<boolean>(false);
  const [changeSubscriptionOpened, setChangeSubscriptionOpened] = useState<boolean>(false);
  const [statusModalOpened, setStatusModalOpened] = useState<boolean>(false);
  const [addUserOpened, setAddUserOpened] = useState<boolean>(false);
  const [statusMode, setStatusMode] = useState<'deactivate' | 'reactivate'>('deactivate');
  const [addGroupOpened, setAddGroupOpened] = useState<boolean>(false);
  const [editSettingsOpened, setEditSettingsOpened] = useState<boolean>(false);
  const [addSchemaOpened, setAddSchemaOpened] = useState<boolean>(false);
  const [addTransitionActionOpened, setAddTransitionActionOpened] = useState<boolean>(false);
  const canChangeTenant = useHasPermission("app.change_tenant");
  const canChangeSubscription = useHasPermission("payment.add_tenantsubscription");
  const canAddUser = useHasPermission("users.add_user");
  const canAddGroup = useHasPermission("auth.add_group");
  const canChangeSettings = useHasPermission("config.change_tenantsettings");
  const canAddSchema = useHasPermission("settlement.add_numerationschema");
  const canAddTransitionAction = useHasPermission("service.add_jobstatustransitionaction");

  const handleOpenStatusModal = (mode: 'deactivate' | 'reactivate') => {
    setStatusMode(mode);
    setStatusModalOpened(true);
  };

  const actions: Action[] = [
    {
      icon: <ApartmentIcon />,
      label: t('Edit workspace'),
      onClick: () => {
        setEditTenantOpened(true);
      },
      disabled: !canChangeTenant,
    },
    {
      icon: <AutorenewIcon />,
      label: t('Change subscription'),
      onClick: () => {
        setChangeSubscriptionOpened(true);
      },
      disabled: !canChangeSubscription,
    },
    {
      icon: <PersonAddIcon />,
      label: t('Add user'),
      onClick: () => {
        setAddUserOpened(true);
      },
      disabled: !canAddUser,
    },
    {
      icon: <SecurityIcon />,
      label: t('Add permission group'),
      onClick: () => {
        setAddGroupOpened(true);
      },
      disabled: !canAddGroup,
    },
    {
      icon: <SettingsIcon />,
      label: t('Edit settings'),
      onClick: () => {
        setEditSettingsOpened(true);
      },
      disabled: !canChangeSettings,
    },
    {
      icon: <NumbersIcon />,
      label: t('Add numeration schema'),
      onClick: () => {
        setAddSchemaOpened(true);
      },
      disabled: !canAddSchema,
    },
    {
      icon: <SyncAltIcon />,
      label: t('Add status transition action'),
      onClick: () => {
        setAddTransitionActionOpened(true);
      },
      disabled: !canAddTransitionAction,
    },
    ...(tenant && tenant.is_active
      ? [
          {
            icon: <PowerSettingsNewIcon />,
            label: t('Deactivate workspace'),
            onClick: () => handleOpenStatusModal('deactivate'),
            disabled: !canChangeTenant,
            color: "var(--errorColor)",
          },
        ]
      : tenant && !tenant.is_active
      ? [
          {
            icon: <RestoreIcon />,
            label: t('Undo deactivation'),
            onClick: () => handleOpenStatusModal('reactivate'),
            disabled: !canChangeTenant,
          },
        ]
      : []),
  ];

  const groupFields: GroupFields[] = [
    {
      title: t('Dashboard configuration'),
      fields: ['contracts_expiring_soon_days'],
    },
    {
      title: t('Numeration schema defaults'),
      fields: [
        'default_job_numeration_schema',
        'default_contract_numeration_schema',
        'default_ordered_part_numeration_schema',
      ],
    },
    {
      title: t('Email server configuration'),
      fields: ['smtp_server', 'smtp_port', 'smtp_username', 'smtp_password'],
    },
    {
      title: t('Counters configuration'),
      fields: ['imap_server', 'imap_port', 'imap_username', 'imap_password', 'counter_outdated_after_days'],
    },
    {
      title: t('Reminders configuration'),
      fields: ['next_reminder_available_after_days', 'reminder_template_title', 'reminder_template_message'],
    },
    {
      title: t('Settlement automation configuration'),
      fields: ['automation_run_day'],
    },
  ];

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={editTenantOpened}
        setOpened={setEditTenantOpened}
        title={t('Edit workspace')}
        content={<EditTenant tenant={tenant} onSuccess={() => {setEditTenantOpened(false); navigate('/tenant')}} />}
      />
      <CustomModal
        opened={changeSubscriptionOpened}
        setOpened={setChangeSubscriptionOpened}
        title={t('Change subscription')}
        content={<ChangeSubscription tenant={tenant} onSuccess={() => {setChangeSubscriptionOpened(false); navigate('/tenant/subscription')}} />}
      />
      <CustomModal
        opened={addUserOpened}
        setOpened={setAddUserOpened}
        title={t('Add user')}
        content={<AddUser onSuccess={() => {setAddUserOpened(false); navigate('/tenant/users')}} />}
      />
      <CustomModal
        opened={addGroupOpened}
        setOpened={setAddGroupOpened}
        title={t('Add permission group')}
        content={<AddPermissionGroup onSuccess={() => {setAddGroupOpened(false); navigate('/tenant/permission-groups')}} />}
      />
      <CustomModal
        opened={statusModalOpened}
        setOpened={setStatusModalOpened}
        title={statusMode === 'deactivate' ? t('Deactivate workspace') : t('Undo deactivation')}
        content={
          <TenantState
            tenant={tenant}
            mode={statusMode}
            onSuccess={() => {setStatusModalOpened(false); navigate('/tenant')}}
          />
        }
      />
      <CustomModal
        opened={editSettingsOpened}
        setOpened={setEditSettingsOpened}
        title={t('Edit settings')}
        content={
          <DynamicForm
            id={"settings"}
            url={TENANT_SETTINGS_ENDPOINT}
            onSuccess={() => {
              setEditSettingsOpened(false);
              window.dispatchEvent(new Event("tenantSettingsUpdated"));
              navigate('/tenant/settings');
            }}
            groupFields={groupFields}
            apiFields={{
              default_job_numeration_schema: {
                url: NUMERATION_SCHEMA_ENDPOINT,
                field: 'name',
              },
              default_contract_numeration_schema: {
                url: NUMERATION_SCHEMA_ENDPOINT,
                field: 'name',
              },
              default_ordered_part_numeration_schema: {
                url: NUMERATION_SCHEMA_ENDPOINT,
                field: 'name',
              },
            }}
            fieldAttributes={{
              imap_password: { type: 'password' },
              smtp_password: { type: 'password' },
              automation_run_day: {
                helperText: t('Day of the month (1-31) on which settlement automation runs for opted-in contracts.'),
              },
              reminder_template_title: {
                multiline: true,
                helperText: t("Tag details are listed below. You can use the same tags in the title."),
              },
              reminder_template_message: {
                multiline: true,
                helperText: (
                  <>
                    {t("Reminder templates support placeholders that are replaced with actual values when sending.")}
                    <br />
                    <strong>{t("Available tags")}:</strong>
                    <br />
                    {"{"}CLIENT_NAME{"}"} - {t("client name")}
                    <br />
                    {"{"}EQUIPMENTS_DETAILS{"}"} - {t("full equipment details (name and serial, bullet list)")}
                    <br />
                    {"{"}EQUIPMENTS_NAMES{"}"} - {t("equipment names (bullet list)")}
                    <br />
                    {"{"}EQUIPMENTS_SERIALS{"}"} - {t("equipment serial numbers (bullet list)")}
                    <br />
                    {"{"}EQUIPMENTS_ADDRESSES{"}"} - {t("equipment installation addresses (bullet list)")}
                    <br />
                    {"{"}COMPANY_NAME{"}"} - {t("workspace or company name")}
                  </>
                ),
              },
            }}
          />
        }
      />
      <CustomModal
        opened={addSchemaOpened}
        setOpened={setAddSchemaOpened}
        title={t('Add numeration schema')}
        content={
          <DynamicForm
            url={NUMERATION_SCHEMA_ENDPOINT}
            onSuccess={() => {
              setAddSchemaOpened(false);
              window.dispatchEvent(new Event("numerationSchemasUpdated"));
              navigate('/tenant/numeration-schemas');
            }}
            fieldAttributes={{
              template: {
                helperText: (
                  <>
                    {t("Template uses placeholders for the number. COUNTER is required. Example: CT/{COUNTER}/{YEAR}/{MONTH}/{DAY}")}
                    <br />
                    <strong>{t("Available tags")}:</strong>
                    <br />
                    {"{"}COUNTER{"}"} — {t("sequential number (required)")}
                    <br />
                    {"{"}YEAR{"}"} — {t("year")}
                    <br />
                    {"{"}MONTH{"}"} — {t("month")}
                    <br />
                    {"{"}DAY{"}"} — {t("day")}
                  </>
                ),
              },
            }}
          />
        }
      />
      <CustomModal
        opened={addTransitionActionOpened}
        setOpened={setAddTransitionActionOpened}
        title={t('Add status transition action')}
        content={
          <StatusTransitionActionForm
            onSuccess={() => {
              setAddTransitionActionOpened(false);
              window.dispatchEvent(new Event("statusTransitionActionsUpdated"));
              navigate('/tenant/status-transition-actions');
            }}
          />
        }
      />
    </>
  );
};
